import React, {Component} from 'react';
import ImageGallery from 'react-image-gallery';
import {connect} from "react-redux";
import {getFromHub} from "../../actions/networkActions";
import {formatPortDate, formatTimeHM, dateStringFromUrlParam} from "../../utils/dateTimeUtils";
import RestaurantEntry from "../../components/RestaurantEntry";
import ModalGallery from "../../components/ModalGallery";
import {imagePath} from "../../utils/imageUtils";
import {formatPortName} from "../../utils/cruiseUtils";
import {getHubBaseUrl, areRestaurantsEnabled, areActivitiesEnabled} from "../../utils/configUtils";
import Exp360VideoPlayerSimple from "../../components/Exp360VideoPlayerSimple";
import BackLink from "../../components/BackLink";
import { Navigate } from 'react-router';
import { Trans } from '@lingui/react/macro';
import { TAC_TEMPLATE_TYPES } from '../../constants/tacConstants';
import { getTranslation } from '../../utils/translationUtils';
import { getBackToCruisePlannerLink } from '../../utils/themeUtils';

class RestaurantsList extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            restaurants: [],
            descriptions: {},
            showGallery: false,
            galleryType: "",
            galleryId: null,
            galleries: {},
            exp360Galleries: {},
            isBookingEnabled: false,
        };
    }

    componentDidMount() {
        if(!this.props.itinerary) {
            return;
        }

        // if itinerary data is present but tac data is not, we can load the tac data the same way we load it on itinerary page
        if(this.props.isTacDataMissing) {
            this.loadTacData();
        }

        this.updateRestaurants(this.props.availableRestaurants || []);
        this.updateConfig(this.props.config || {});
    }

    // same three calls the cruise planner makes on mount with some extra logic in there. TODO - consider extracting this logic to helper functions
    loadTacData = () => {
        const isActivity = this.props.isActivity;

        getFromHub(
            isActivity
                ? 'prebooking/tac/templates/types/activity/availability'
                : 'prebooking/tac/templates/types/restaurant/availability',
            availabilityByDate => this.props.dispatch({
                type: isActivity ? 'SET_TAC_ACTIVITIES_AVAILABILITY' : 'SET_TAC_RESTAURANTS_AVAILABILITY',
                payload: availabilityByDate || {},
            })
        );

        getFromHub('prebooking/tac/config', config => {
            const payload = {
                isRestaurantsEnabled: areRestaurantsEnabled() ? config.diningEnabled : false,
                isRestaurantsBookingEnabled: areRestaurantsEnabled() ? config.diningBookingEnabled : false,
                isActivitiesEnabled: areActivitiesEnabled() ? config.activityEnabled : false,
                isActivitiesBookingEnabled: areActivitiesEnabled() ? config.activityBookingEnabled : false,
            };

            this.props.dispatch({ type: 'SET_TAC_CONFIG', payload });
            this.updateConfig(payload);
        });
    }

    componentDidUpdate(prevProps) {
        if (this.props.availableRestaurants !== prevProps.availableRestaurants) {
            this.updateRestaurants(this.props.availableRestaurants || []);
        }
    }

    updateRestaurants = (restaurants) => {
        let restaurantsInfo = [];
        restaurants.forEach(restaurant => {
            if(restaurant.serviceProfiles && restaurant.serviceProfiles.length > 0) {
                //add each service profile as separate restaurant
                restaurant.serviceProfiles.forEach((serviceProfile) => {
                    restaurantsInfo.push({
                        templateId: restaurant.id,
                        location: restaurant.location,
                        ...serviceProfile
                    });
                });
            }
        });

        this.setState({
            restaurants: restaurantsInfo,
            loaded: true,
            descriptions: {},
        }, () => {
            if(restaurantsInfo.length > 0) {
                this.fetchDescriptions();
            }
        });
    };

    updateConfig = (config) => {
        const isActivity = this.props.isActivity;
        this.setState({
            isBookingEnabled: isActivity ? config.isActivitiesBookingEnabled : config.isRestaurantsBookingEnabled,
        });
    }

    fetchDescriptions = () => {
        const ids = this.state.restaurants.map((restaurant) => restaurant.id).join(",");
        getFromHub(`descriptions/${ids}`, this.updateDescriptions);
    };

    updateDescriptions = (descriptions) => {
        const mappedDescriptions = {};

        descriptions.forEach((description) => {
            if(!mappedDescriptions[description.reference]) {
                mappedDescriptions[description.reference] = {};
            }

            mappedDescriptions[description.reference][description.descriptionType.toLowerCase()] = getTranslation(description.value);
        });

        this.setState({
            descriptions: mappedDescriptions,
        });
    };

    toggleGallery = (id, isExp360Gallery) => {
        if(this.state.showGallery) {
            this.setState({
                showGallery: false,
                galleryId: null,
                galleryType: "",
            });
        }
        else {
            if(isExp360Gallery) {
                //exp360 playlist id already fetched?
                if(this.state.exp360Galleries[id]) {
                    this.showGallery(id, "exp360");
                }
                else {
                    this.fetchExp360BundleId(id);
                }
            }
            else {
                //list of gallery images already fetched?
                if(this.state.galleries[id]) {
                    this.showGallery(id, "standard");
                }
                else {
                    this.fetchImagesListForGallery(id);
                }
            }
        }
    };



    fetchImagesListForGallery = (id) => {
        getFromHub(
            `blobs/images/${id}`,
            (imagesResponse) => this.updateImagesForGallery(id, imagesResponse)
        )
    };

    fetchExp360BundleId = async id => {
        const response = await getFromHub(`exp360/${id}`);
        let exp360BundleId = "";
        if(response.length) {
            exp360BundleId = response[0].snippet;
        }

        if(exp360BundleId === "") {
            this.setState({
                exp360Galleries: {...this.state.exp360Galleries, [id]: exp360BundleId}
            });
        }
        else {
            this.setState({
                exp360Galleries: {...this.state.exp360Galleries, [id]: exp360BundleId}
            }, () => this.showGallery(id, "exp360"));
        }
    };

    updateImagesForGallery = (id, imagesResponse) => {
        const images = imagesResponse.map((image) => {
            return {
                thumbnail: `${getHubBaseUrl(false)}${image.url}&width=200`,
                original: `${getHubBaseUrl(false)}${image.url}`,
            }
        });

        const galleries = {...this.state.galleries};
        galleries[id] = images;

        this.setState({
            galleries
        }, () => this.showGallery(id, "standard"))
    };

    showGallery = (id, type) => {
        this.setState({
            showGallery: true,
            galleryType: type,
            galleryId: id,
        });
    };

    render() {
        const {date, isActivity} = this.props;

        // if date is missing or itinerary was not loaded yet (because user came directly using a link/bookmark),
        // we redirect them to itinerary page where it will be loaded
        if(!this.props.itinerary) {
            return (
                <Navigate to={getBackToCruisePlannerLink()} replace />
            );
        }

        if(!this.state.loaded)  {
            return null;
        }

        if(this.state.loaded && this.state.restaurants.length === 0)  {
            return (
                <div className="container mx-auto my-4">
                    <span>
                        {isActivity
                            ? <Trans id="no_activities_available">Unfortunately, Activities Onboard are not yet available to book on your chosen date, please check back again later.</Trans>
                            : <Trans id="no_speciality_dining_available">Unfortunately, Speciality Dining is not yet available to book on your chosen date, please check back again later.</Trans>
                        }
                    </span>
                </div>
            );
        }

        const day = this.props.itinerary.dayOfCruise;
        const port = formatPortName(this.props.itinerary.type, this.props.itinerary.port);

        return (
            <div>
                <div className="container mx-auto mt-4 text-primary-dark">
                    <BackLink to={getBackToCruisePlannerLink()} text={<Trans id="back_to_excursions_list">Cruise Planner</Trans>}/>
                    <h1>
                        {isActivity
                            ? <Trans id="activities_onboard_heading">Activities Onboard</Trans>
                            : <Trans id="restaurant_options_heading">Restaurant options</Trans>
                        }
                    </h1>
                    <div className="text-2xl">
                        <Trans id="day_number_date">DAY {day} ({formatPortDate(date)})</Trans>
                    </div>
                    <div className="text-3xl mt-1">
                        {port}
                    </div>
                    {this.props.itinerary.type === "TenderPort" &&
                        <div className="text-3xl mt-1">
                            <Trans id="time_in_port">Time in port: {formatTimeHM(this.props.itinerary.arrival, "Previous day")} - {formatTimeHM(this.props.itinerary.departure, "Next day")}</Trans>
                        </div>
                    }

                    <div className="print-hidden">
                        <ModalGallery
                            show={this.state.showGallery}
                            onClose={this.toggleGallery}
                        >
                            {this.state.galleryType === "standard" &&
                            <ImageGallery
                                items={this.state.galleries[this.state.galleryId]}
                                lazyLoad={true}
                                showIndex={true}
                                defaultImage={imagePath('2_1_no_image.png')}
                                showPlayButton={false}
                            />
                            }

                            {this.state.galleryType === "exp360" &&
                                <Exp360VideoPlayerSimple bundleId={this.state.exp360Galleries[this.state.galleryId]}/>
                            }
                        </ModalGallery>

                    </div>
                </div>

                <div className="bg-primary-lightest-25">
                    <div className="container mx-auto px-2 sm:px-4 pb-8">
                        {this.state.restaurants.map(restaurant => (
                            <RestaurantEntry
                                key={restaurant.id}
                                restaurant={restaurant}
                                descriptions={this.state.descriptions[restaurant.id]}
                                toggleGallery={() => this.toggleGallery(restaurant.id, false)}
                                toggleExp360Gallery={
                                    restaurant.exp360Available ?
                                        () => this.toggleGallery(restaurant.id, true):
                                        false
                                }
                                date={date}
                                preselectedDetails={this.props.location?.state?.preselectedDetails}
                                isBookingEnabled={this.state.isBookingEnabled}
                                isActivity={isActivity}
                            />
                            )
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state, ownProps) {
    const propsDate = dateStringFromUrlParam(ownProps.params?.date);
    const isActivity = ownProps.params?.type === TAC_TEMPLATE_TYPES.ACTIVITY;

    const availabilityByDate = isActivity
        ? state.tac.activitiesAvailabilityByDate
        : state.tac.restaurantsAvailabilityByDate;

    return {
        date: propsDate,
        isActivity,
        itinerary: state.itinerary.find(({date}) => date === propsDate),
        availableRestaurants: availabilityByDate?.[propsDate] || [],
        // an empty map means the planner has not been opened in this session
        isTacDataMissing: Object.keys(availabilityByDate || {}).length === 0,
        config: state.tac.config || {},
    }
}

export default connect(mapStateToProps)(RestaurantsList);



